import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Banner from '../components/common/Banner';
import SectionTitle from '../components/common/SectionTitle';
import Accordion from '../components/common/Accordion';
import CallToAction from '../components/common/CallToAction';
import { MapPin, Calendar, Phone, Mail, Globe, Check, FileText, Clock, Award, BookOpen, Users } from 'lucide-react';

// College data
const collegeData = {
  'sourabh': {
    name: 'Sourabh College of Veterinary Science',
    tagline: 'Excellence in Veterinary Education and Research',
    location: 'Hindaun City, Rajasthan',
    establishedYear: '2012',
    contactPhone: '+91 1469 234567',
    contactEmail: 'admissions@scvs.edu.in',
    website: 'www.scvs.edu.in',
    description: `Sourabh College of Veterinary Science (SCVS) is a premier institution dedicated to providing quality education in veterinary science. Located in Hindaun City, Rajasthan, the college offers a comprehensive BVSc & AH program that combines theoretical knowledge with practical training.

The college features state-of-the-art laboratories, a well-equipped veterinary hospital, and modern classrooms to ensure students receive the best education possible. The faculty consists of experienced veterinarians and researchers committed to academic excellence and student success.

SCVS is recognized by the Veterinary Council of India (VCI) and is affiliated with a prominent agricultural university, ensuring that the degree awarded is recognized nationwide. The college maintains strong industry connections to provide students with internship opportunities and placement assistance.`,
    facilities: [
      'Modern veterinary hospital with advanced diagnostic equipment',
      'Specialized laboratories for pathology, microbiology, and parasitology',
      'Well-stocked library with extensive veterinary literature',
      'Computer lab with internet access for research and assignments',
      'Spacious classrooms with audio-visual teaching aids',
      'Separate hostels for male and female students',
      'Sports facilities including indoor and outdoor games',
      'Campus cafeteria serving nutritious meals',
      'Transportation facilities for students and staff'
    ],
    admissionProcess: [
      'Qualification in NEET-UG with minimum required percentile',
      'Registration for counseling through the centralized admission process',
      'Document verification and eligibility check',
      'Seat allotment based on merit and preferences',
      'Fee payment and admission confirmation',
      'Medical examination and hostel allocation'
    ],
    coursesOffered: [
      {
        name: 'Bachelor of Veterinary Science & Animal Husbandry (BVSc & AH)',
        duration: '5.5 years (including 6 months internship)',
        seats: '100 seats per year',
        eligibility: '10+2 with PCB, minimum 50% marks, NEET qualification'
      }
    ],
    feeStructure: {
      tuitionFee: '₹4,50,000 per year',
      hostelFee: '₹75,000 per year',
      messFee: '₹60,000 per year',
      otherCharges: '₹25,000 per year',
      totalAnnual: '₹6,10,000 per year'
    },
    scholarships: [
      'Merit-based scholarships for top performers',
      'Financial aid for economically weaker sections',
      'Special scholarships for female students',
      'Sports and cultural achievement scholarships'
    ],
    placementOpportunities: [
      'Government veterinary services',
      'Private veterinary clinics and hospitals',
      'Pharmaceutical companies',
      'Research institutions',
      'Livestock farms and dairy industry',
      'Pet food companies',
      'Animal welfare organizations',
      'Higher studies in India and abroad'
    ],
    facultyStrength: '45+ qualified faculty members with specializations in various veterinary disciplines',
    accreditation: 'Recognized by Veterinary Council of India (VCI) and affiliated with Rajasthan University of Veterinary and Animal Sciences',
    images: [
      'https://images.pexels.com/photos/1472462/pexels-photo-1472462.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      'https://images.pexels.com/photos/5327584/pexels-photo-5327584.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      'https://images.pexels.com/photos/5327921/pexels-photo-5327921.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    ],
    faqs: [
      {
        id: 'faq-1',
        title: 'What is the NEET score required for admission to SCVS?',
        content: 'The NEET score requirement varies each year based on competition. Generally, a minimum of 50th percentile is required for general category students and 40th percentile for reserved categories. However, for actual admission, the cut-off tends to be higher depending on the number of applicants. We recommend aiming for at least 500+ marks in NEET for a competitive chance at admission.'
      },
      {
        id: 'faq-2',
        title: 'Are there any management or NRI quota seats available?',
        content: 'Yes, SCVS reserves a certain percentage of seats under management quota and NRI quota as per regulatory guidelines. These seats have a different fee structure and admission process. For management quota, students still need to qualify NEET, while having slightly lower cut-offs. For NRI quota, specific eligibility criteria apply. Contact our admissions office for detailed information on these quotas.'
      },
      {
        id: 'faq-3',
        title: 'What are the hostel facilities like at SCVS?',
        content: 'SCVS provides separate hostel facilities for male and female students with well-furnished rooms available on sharing basis (2 or 3 students per room). Hostels include amenities such as 24-hour electricity with backup, clean drinking water, hot water facilities, Wi-Fi, common rooms with TV, reading rooms, washing machines, and security. Mess facilities provide nutritious vegetarian and non-vegetarian meal options.'
      },
      {
        id: 'faq-4',
        title: 'What is the scope after completing BVSc from SCVS?',
        content: 'Graduates from SCVS have excellent career prospects. Many join government veterinary services through competitive exams, while others establish private clinics or join corporate hospitals. Some enter pharmaceutical companies, research institutions, or the food safety sector. The college has a dedicated placement cell that assists students with job placements and higher education opportunities. Many alumni have also successfully pursued master\'s and doctoral programs in India and abroad.'
      }
    ]
  },
  'rps': {
    name: 'RPS College of Veterinary Science',
    tagline: 'Shaping the Future of Veterinary Care',
    location: 'Mahendergarh, Haryana',
    establishedYear: '2010',
    contactPhone: '+91 1285 456789',
    contactEmail: 'info@rpscvs.edu.in',
    website: 'www.rpscvs.edu.in',
    description: `RPS College of Veterinary Science is a leading institution for veterinary education located in Mahendergarh, Haryana. Established in 2010, the college has quickly gained recognition for its academic excellence and modern approach to veterinary education.

The college offers a comprehensive BVSc & AH program designed to equip students with theoretical knowledge and practical skills needed for a successful career in veterinary science. The curriculum is regularly updated to incorporate the latest developments in the field.

RPS College boasts state-of-the-art infrastructure including modern laboratories, a well-equipped veterinary hospital, and specialized research centers. The college is situated on a sprawling campus with excellent facilities for both academic and extracurricular activities.

The faculty comprises experienced veterinarians, researchers, and academicians who are committed to providing quality education and mentoring students. The college maintains a low student-to-faculty ratio to ensure personalized attention and guidance.`,
    facilities: [
      'Advanced veterinary teaching hospital with specialized departments',
      'Modern diagnostic laboratories with the latest equipment',
      'Animal farm with various livestock species for hands-on training',
      'Digital library with access to international veterinary journals',
      'Simulation lab for practical training before live animal handling',
      'Comfortable hostels with Wi-Fi and recreational facilities',
      'Sports complex with indoor and outdoor facilities',
      'Auditorium for academic and cultural events',
      'Canteen serving diverse and nutritious food options'
    ],
    admissionProcess: [
      'Qualification in NEET-UG with required percentile',
      'Application through the centralized counseling process',
      'Merit-based selection according to NEET score',
      'Document verification and medical examination',
      'Fee payment and registration',
      'Orientation program for new students'
    ],
    coursesOffered: [
      {
        name: 'Bachelor of Veterinary Science & Animal Husbandry (BVSc & AH)',
        duration: '5.5 years (including 6 months internship)',
        seats: '80 seats per year',
        eligibility: '10+2 with PCB, minimum 50% marks, NEET qualification'
      }
    ],
    feeStructure: {
      tuitionFee: '₹3,80,000 per year',
      hostelFee: '₹60,000 per year',
      messFee: '₹55,000 per year',
      otherCharges: '₹20,000 per year',
      totalAnnual: '₹5,15,000 per year'
    },
    scholarships: [
      'Merit scholarships for top-ranking students',
      'Scholarships for students from rural backgrounds',
      'Financial assistance for economically disadvantaged students',
      'Special scholarships for women in veterinary science'
    ],
    placementOpportunities: [
      'Government veterinary departments',
      'Private veterinary hospitals and clinics',
      'Pharmaceutical and animal health companies',
      'Poultry and dairy industries',
      'Research and development organizations',
      'Wildlife conservation organizations',
      'Armed forces veterinary corps',
      'International opportunities through exchange programs'
    ],
    facultyStrength: '40+ highly qualified faculty members with diverse specializations',
    accreditation: 'Recognized by Veterinary Council of India (VCI) and affiliated with Lala Lajpat Rai University of Veterinary and Animal Sciences',
    images: [
      'https://images.pexels.com/photos/3786157/pexels-photo-3786157.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      'https://images.pexels.com/photos/5731866/pexels-photo-5731866.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      'https://images.pexels.com/photos/5327640/pexels-photo-5327640.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    ],
    faqs: [
      {
        id: 'faq-1',
        title: 'Does RPS College offer any specialization tracks within the BVSc program?',
        content: 'While the BVSc & AH program follows a standardized curriculum as per VCI guidelines, RPS College offers additional certificate courses and electives in specialized areas such as pet care, equine medicine, wildlife medicine, and livestock management. Students can choose these electives in their final year to develop expertise in their areas of interest. The college also organizes specialized workshops and training programs by industry experts.'
      },
      {
        id: 'faq-2',
        title: 'What kind of practical exposure do students get at RPS College?',
        content: 'RPS College places strong emphasis on practical training. Students get hands-on experience through rotations in the teaching veterinary hospital, field visits to dairy farms, poultry units, and wildlife centers. The college also has mandatory rural veterinary practice programs where students work in village settings. Additionally, the college arranges industrial visits to pharmaceutical companies, food processing units, and research laboratories to provide comprehensive exposure to various aspects of veterinary science.'
      },
      {
        id: 'faq-3',
        title: 'Are there opportunities for research and publication for undergraduate students?',
        content: 'Yes, RPS College actively encourages undergraduate research. The college has a Student Research Scheme where interested students can work on research projects under faculty guidance. Students can present their research at the annual college symposium and at national student conferences. Exceptional projects may receive funding support. Many students have co-authored research papers published in reputable veterinary journals, giving them an advantage for higher studies and specialized careers.'
      },
      {
        id: 'faq-4',
        title: 'What support services are available for students from other states?',
        content: 'RPS College has a dedicated student welfare cell that assists students from other states with accommodation, local language support, and cultural integration. The college offers language assistance for students not familiar with the local language. There\'s also a buddy system where senior students from the same region are assigned to help new students adjust. The international and interstate student association organizes cultural events to celebrate diversity and help students feel at home.'
      }
    ]
  },
  'abhilashi': {
    name: 'Abhilashi Veterinary College',
    tagline: 'Nurturing Compassionate Veterinary Professionals',
    location: 'Mandi, Himachal Pradesh',
    establishedYear: '2008',
    contactPhone: '+91 1905 789012',
    contactEmail: 'admissions@abhilashivet.edu.in',
    website: 'www.abhilashivet.edu.in',
    description: `Abhilashi Veterinary College (AVC) is a premier veterinary institution located in the picturesque surroundings of Mandi, Himachal Pradesh. Established in 2008, the college has established itself as a center of excellence for veterinary education in the Himalayan region.

The college is known for its holistic approach to veterinary education, combining traditional knowledge with modern scientific advancements. The BVSc & AH program offered by AVC is designed to develop skilled veterinarians who are compassionate, knowledgeable, and equipped to address the unique challenges of animal healthcare in mountainous and rural regions.

Located amidst the natural beauty of Himachal Pradesh, the campus provides an ideal environment for learning and research. The college maintains close ties with local animal husbandry departments, farming communities, and wildlife conservation organizations, providing students with diverse practical experiences.

AVC boasts modern infrastructure, well-equipped laboratories, and experienced faculty members who are dedicated to student success. The college places special emphasis on research related to high-altitude animal health, indigenous livestock breeds, and sustainable veterinary practices.`,
    facilities: [
      'Modern veterinary hospital with specialized departments and OPD facilities',
      'Well-equipped laboratories for pathology, microbiology, parasitology, and more',
      'Livestock farm with local and exotic breeds for practical training',
      'Digital library with extensive collection of veterinary resources',
      'Specialized research center for high-altitude animal health studies',
      'Comfortable residential facilities for students and faculty',
      'Recreational areas including sports grounds and indoor game facilities',
      'Student activity center for extracurricular development',
      'In-campus medical facilities for healthcare needs'
    ],
    admissionProcess: [
      'Qualification in NEET-UG with required percentile',
      'Application through state counseling or management quota process',
      'Merit-based selection as per state and institutional policies',
      'Document verification and eligibility confirmation',
      'Fee payment and admission formalization',
      'Hostel allocation and orientation program'
    ],
    coursesOffered: [
      {
        name: 'Bachelor of Veterinary Science & Animal Husbandry (BVSc & AH)',
        duration: '5.5 years (including 6 months internship)',
        seats: '60 seats per year',
        eligibility: '10+2 with PCB, minimum 50% marks, NEET qualification'
      }
    ],
    feeStructure: {
      tuitionFee: '₹3,20,000 per year',
      hostelFee: '₹50,000 per year',
      messFee: '₹48,000 per year',
      otherCharges: '₹15,000 per year',
      totalAnnual: '₹4,33,000 per year'
    },
    scholarships: [
      'Merit-based institutional scholarships',
      'Hill state student scholarships',
      'Scholarships for students from farming families',
      'Financial aid for economically weaker sections',
      'Special incentives for students pursuing research in local animal breeds and diseases'
    ],
    placementOpportunities: [
      'State animal husbandry departments',
      'Private veterinary clinics and hospitals',
      'Pharmaceutical and animal health product companies',
      'Dairy development corporations',
      'Wildlife conservation organizations',
      'Research institutions and universities',
      'NGOs working in animal welfare',
      'Opportunities for self-employment and entrepreneurship'
    ],
    facultyStrength: '35+ dedicated faculty members with specializations in various veterinary disciplines',
    accreditation: 'Recognized by Veterinary Council of India (VCI) and affiliated with Himachal Pradesh Agriculture University',
    images: [
      'https://images.pexels.com/photos/267885/pexels-photo-267885.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      'https://images.pexels.com/photos/3861458/pexels-photo-3861458.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
      'https://images.pexels.com/photos/5726837/pexels-photo-5726837.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    ],
    faqs: [
      {
        id: 'faq-1',
        title: 'How does Abhilashi Veterinary College address the challenges of studying in a mountainous region?',
        content: 'Abhilashi Veterinary College has developed specific programs to address the unique challenges and opportunities of its mountainous location. The college provides heated accommodation during winter months, reliable internet connectivity despite the remote location, and transportation services for field visits. The curriculum includes special modules on high-altitude animal health, mountain livestock management, and local disease patterns. These geographical challenges are transformed into learning opportunities through research projects focused on indigenous mountain breeds and their adaptations.'
      },
      {
        id: 'faq-2',
        title: 'What special programs does AVC offer related to indigenous animal breeds?',
        content: 'AVC has a dedicated Indigenous Breed Conservation Center that focuses on studying, preserving, and promoting native livestock breeds of the Himalayan region. Students can participate in research projects documenting the genetic makeup, disease resistance, and productivity of local breeds like the Gaddi sheep, Chamba Chamois goat, and Spiti horse. The college also runs extension programs in collaboration with local farmers to promote sustainable rearing of these breeds. Students interested in indigenous animal genetics and conservation find these programs particularly valuable.'
      },
      {
        id: 'faq-3',
        title: 'Does the college offer any unique courses related to wildlife or mountain ecosystems?',
        content: 'Yes, AVC offers specialized elective modules in wildlife medicine with a focus on Himalayan species. The college has partnerships with local wildlife sanctuaries and the state forest department, providing students with opportunities for wildlife health monitoring, rescue operations, and conservation projects. Special workshops on wildlife immobilization, health assessment, and habitat management are regularly conducted by visiting experts. The college also offers a certificate course in mountain ecosystem management that students can pursue alongside their regular curriculum.'
      },
      {
        id: 'faq-4',
        title: 'What accommodation options are available for students from other states?',
        content: 'AVC provides comfortable hostel facilities for all students, with special consideration for those from distant states. The hostels are designed to withstand the cold climate with proper heating arrangements and hot water facilities. Rooms are available on double or triple sharing basis, with limited single occupancy options for senior students. The hostels have Wi-Fi connectivity, study rooms, recreation areas, and 24-hour security. Cultural integration programs are organized to help students from different regions adjust to the local environment and culture.'
      }
    ]
  }
};

interface CollegeDetailPageProps {
  collegeId: string;
}

const CollegeDetailPage: React.FC<CollegeDetailPageProps> = ({ collegeId }) => {
  const navigate = useNavigate();
  const college = collegeData[collegeId as keyof typeof collegeData];
  
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    // Redirect if college doesn't exist
    if (!college) {
      navigate('/colleges', { replace: true });
      return;
    }
    
    // Update document title
    document.title = `${college.name} - VetEdu Consultants`;
  }, [college, navigate, collegeId]);

  if (!college) return null;

  return (
    <>
      <Banner 
        title={college.name}
        subtitle={college.tagline}
        backgroundImage={college.images[0]}
      />
      
      <section className="section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold mb-6 text-primary-700">About the College</h2>
              
              <div className="prose max-w-none mb-8 text-gray-700">
                {college.description.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-4">{paragraph}</p>
                ))}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Calendar size={20} className="text-primary-500 mr-2" />
                    <h3 className="font-semibold">Established</h3>
                  </div>
                  <p className="text-gray-700">{college.establishedYear}</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Users size={20} className="text-primary-500 mr-2" />
                    <h3 className="font-semibold">Faculty</h3>
                  </div>
                  <p className="text-gray-700">{college.facultyStrength.split(' ')[0]}</p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Award size={20} className="text-primary-500 mr-2" />
                    <h3 className="font-semibold">Accreditation</h3>
                  </div>
                  <p className="text-gray-700">VCI Recognized</p>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold mb-4 text-primary-700">Facilities & Infrastructure</h3>
              
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                {college.facilities.map((facility, index) => (
                  <li key={index} className="flex items-start">
                    <Check size={18} className="text-primary-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{facility}</span>
                  </li>
                ))}
              </ul>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {college.images.slice(1).map((image, index) => (
                  <div key={index} className="rounded-lg overflow-hidden shadow-md">
                    <img 
                      src={image} 
                      alt={`${college.name} Campus ${index + 1}`} 
                      className="w-full h-64 object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
                <div className="bg-primary-700 p-4 text-white">
                  <h3 className="text-lg font-semibold">Contact Information</h3>
                </div>
                
                <div className="p-6">
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <MapPin size={20} className="text-primary-500 mr-3 mt-1" />
                      <div>
                        <p className="font-medium text-gray-800">Address</p>
                        <p className="text-gray-600">{college.name}, {college.location}</p>
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <Phone size={20} className="text-primary-500 mr-3 mt-1" />
                      <div>
                        <p className="font-medium text-gray-800">Phone</p>
                        <a href={`tel:${college.contactPhone}`} className="text-gray-600 hover:text-primary-600">
                          {college.contactPhone}
                        </a>
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <Mail size={20} className="text-primary-500 mr-3 mt-1" />
                      <div>
                        <p className="font-medium text-gray-800">Email</p>
                        <a href={`mailto:${college.contactEmail}`} className="text-gray-600 hover:text-primary-600">
                          {college.contactEmail}
                        </a>
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <Globe size={20} className="text-primary-500 mr-3 mt-1" />
                      <div>
                        <p className="font-medium text-gray-800">Website</p>
                        <a href={`https://${college.website}`} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-primary-600">
                          {college.website}
                        </a>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
                <div className="bg-primary-700 p-4 text-white">
                  <h3 className="text-lg font-semibold">Quick Inquiry</h3>
                </div>
                
                <div className="p-6">
                  <form className="space-y-4">
                    <div>
                      <input
                        type="text"
                        placeholder="Your Name"
                        className="form-input"
                        required
                      />
                    </div>
                    
                    <div>
                      <input
                        type="tel"
                        placeholder="Phone Number"
                        className="form-input"
                        required
                      />
                    </div>
                    
                    <div>
                      <input
                        type="email"
                        placeholder="Email Address"
                        className="form-input"
                        required
                      />
                    </div>
                    
                    <div>
                      <textarea
                        placeholder="Your Message"
                        rows={4}
                        className="form-input"
                        required
                      ></textarea>
                    </div>
                    
                    <button type="submit" className="btn btn-primary w-full">
                      Submit Inquiry
                    </button>
                  </form>
                </div>
              </div>
              
              <div className="bg-primary-50 rounded-lg p-6 border border-primary-100">
                <div className="flex items-center mb-4">
                  <Clock size={24} className="text-primary-500 mr-3" />
                  <h3 className="text-lg font-semibold text-primary-700">Admission Timeline</h3>
                </div>
                
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs mt-1 mr-2">
                      1
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">NEET Examination</p>
                      <p className="text-gray-600 text-sm">May - June</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs mt-1 mr-2">
                      2
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Result Declaration</p>
                      <p className="text-gray-600 text-sm">June - July</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs mt-1 mr-2">
                      3
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Counseling Process</p>
                      <p className="text-gray-600 text-sm">July - August</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs mt-1 mr-2">
                      4
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Admission Confirmation</p>
                      <p className="text-gray-600 text-sm">August - September</p>
                    </div>
                  </li>
                  
                  <li className="flex items-start">
                    <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs mt-1 mr-2">
                      5
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">Session Commencement</p>
                      <p className="text-gray-600 text-sm">September - October</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-2xl font-bold mb-6 text-primary-700">Admission Process</h2>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold mb-4 text-primary-700">
                  Course Offered
                </h3>
                
                {college.coursesOffered.map((course, index) => (
                  <div key={index} className="mb-6 last:mb-0">
                    <h4 className="font-medium text-gray-800 mb-2">{course.name}</h4>
                    
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <Clock size={16} className="text-primary-500 mr-2 mt-1" />
                        <span className="text-gray-700">Duration: {course.duration}</span>
                      </li>
                      
                      <li className="flex items-start">
                        <Users size={16} className="text-primary-500 mr-2 mt-1" />
                        <span className="text-gray-700">Seats: {course.seats}</span>
                      </li>
                      
                      <li className="flex items-start">
                        <FileText size={16} className="text-primary-500 mr-2 mt-1" />
                        <span className="text-gray-700">Eligibility: {course.eligibility}</span>
                      </li>
                    </ul>
                  </div>
                ))}
                
                <h3 className="text-lg font-semibold mb-4 text-primary-700 mt-8">
                  Admission Steps
                </h3>
                
                <ul className="space-y-3">
                  {college.admissionProcess.map((step, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold mr-3 mt-0.5">
                        {index + 1}
                      </div>
                      <span className="text-gray-700">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div>
              <h2 className="text-2xl font-bold mb-6 text-primary-700">Fee Structure</h2>
              
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-primary-700 text-white">
                      <th className="py-3 px-4 text-left">Fee Component</th>
                      <th className="py-3 px-4 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-800">Tuition Fee</td>
                      <td className="py-3 px-4 text-right text-gray-800">{college.feeStructure.tuitionFee}</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-800">Hostel Fee</td>
                      <td className="py-3 px-4 text-right text-gray-800">{college.feeStructure.hostelFee}</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-800">Mess Fee</td>
                      <td className="py-3 px-4 text-right text-gray-800">{college.feeStructure.messFee}</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-800">Other Charges</td>
                      <td className="py-3 px-4 text-right text-gray-800">{college.feeStructure.otherCharges}</td>
                    </tr>
                    <tr className="bg-gray-100 font-semibold">
                      <td className="py-3 px-4 text-gray-800">Total Annual Fee</td>
                      <td className="py-3 px-4 text-right text-gray-800">{college.feeStructure.totalAnnual}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6 mt-8">
                <h3 className="text-lg font-semibold mb-4 text-primary-700">
                  Scholarship Opportunities
                </h3>
                
                <ul className="space-y-3">
                  {college.scholarships.map((scholarship, index) => (
                    <li key={index} className="flex items-start">
                      <Award size={18} className="text-primary-500 mr-2 mt-1" />
                      <span className="text-gray-700">{scholarship}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section">
        <div className="container-custom">
          <SectionTitle 
            title="Career Opportunities" 
            subtitle="Where our graduates find success after completing their BVSc degree"
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {college.placementOpportunities.map((opportunity, index) => (
              <div key={index} className="bg-white p-5 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen size={24} className="text-primary-600" />
                </div>
                <h4 className="font-semibold mb-2">{opportunity}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="section bg-gray-50">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle={`Common questions about ${college.name}`}
          />
          
          <div className="max-w-3xl mx-auto mt-8">
            <Accordion items={college.faqs} />
          </div>
        </div>
      </section>
      
      <CallToAction 
        title={`Interested in ${college.name}?`}
        description="Contact us today for expert guidance on admission procedures and application assistance."
        buttonText="Get Admission Guidance"
        buttonLink="/contact"
      />
    </>
  );
};

export default CollegeDetailPage;